﻿if (!customElements.get('outdoor-kitchen-designer')) {
  customElements.define('outdoor-kitchen-designer', class extends HTMLElement {
    connectedCallback() {
      this.controller = new AbortController();
      this.cabinets = [];
      this.selectedCabinet = null;
      this.selectedColor = this.querySelector('[data-color="#c7beb7"]');
      this.doorStyle = 'Sanibel';
      this.previewProducts = new Map();
      this.previewVersion = 0;
      this.endPanels = [];
      this.interiorPrefetches = [];
      const header = document.getElementById('SiteHeader');
      const updateTogglePosition = () => {
        cancelAnimationFrame(this.sidebarFrame);
        this.sidebarFrame = requestAnimationFrame(() => {
          const bounds = this.getBoundingClientRect();
          const menuBottom = Math.max(0, header.getBoundingClientRect().bottom);
          const offset = Math.min(Math.max(0, bounds.height - 48), Math.max(0, menuBottom - bounds.top));
          this.style.setProperty('--designer-menu-offset', `${offset}px`);
        });
      };
      this.headerObserver = new MutationObserver(updateTogglePosition);
      this.headerObserver.observe(header, { attributes: true, attributeFilter: ['class'] });
      window.addEventListener('scroll', updateTogglePosition, { passive: true, signal: this.controller.signal });
      window.addEventListener('resize', updateTogglePosition, { signal: this.controller.signal });
      updateTogglePosition();
      try {
        this.initScene();
        this.updateDoorPreviews();
      } catch (error) {
        this.setLoading(error.message, false);
        return;
      }
      this.querySelector('.designer-ar').addEventListener('close', () => this.clearARPreview(), { signal: this.controller.signal });
      this.addEventListener('click', (event) => {
        if (event.target.closest('[data-view-ar]')) this.openAR();
        if (event.target.closest('[data-close-ar]')) this.querySelector('.designer-ar').close();
        const toggle = event.target.closest('.designer-toggle');
        if (toggle) {
          const expanded = toggle.getAttribute('aria-expanded') === 'true';
          toggle.setAttribute('aria-expanded', String(!expanded));
          toggle.setAttribute('aria-label', expanded ? 'Expand design options' : 'Collapse design options');
          this.querySelector('.designer-options').inert = expanded;
          this.querySelector('.designer-options').setAttribute('aria-hidden', String(expanded));
          this.querySelector('.designer-sidebar').classList.toggle('is-collapsed', expanded);
          return;
        }
        const color = event.target.closest('[data-color]');
        const doorStyle = event.target.closest('[data-door-style]');
        if (doorStyle) {
          this.doorStyle = doorStyle.dataset.doorStyle;
          this.querySelector('[data-door-style-name]').textContent = this.doorStyle;
          this.cabinets.forEach((cabinet) => this.applyDoorStyle(cabinet.container));
          this.querySelectorAll('[data-door-style]').forEach((button) => {
            button.setAttribute('aria-pressed', String(button.dataset.doorStyle === this.doorStyle));
          });
        }
        const countertop = event.target.closest('[data-countertop]');
        if (countertop) this.applyCountertop(countertop);
        if (color) {
          this.applyColor(color);
        }
        const reset = event.target.closest('[data-reset-color]');
        if (reset) {
          this.applyColor(null);
        }
        const interior = event.target.closest('[data-interior]');
        if (interior) this.replaceInterior(this.selectedCabinet, interior.dataset.interior);
        const model = event.target.closest('[data-model]');
        if (model) this.addCabinet(model.dataset.model);
        if (event.target.closest('[data-toggle-door]')) this.toggleAllDoors();
        if (event.target.closest('[data-toggle-selected-door]') && this.selectedCabinet) {
          this.setDoors(this.selectedCabinet, !this.selectedCabinet.isOpen);
        }
        if (event.target.closest('[data-delete-selected]') && this.selectedCabinet) {
          const cabinet = this.selectedCabinet;
          this.selectCabinet(null);
          this.cabinets.splice(this.cabinets.indexOf(cabinet), 1);
          cabinet.root.dispose();
          if (cabinet.interior) cabinet.interior.container.dispose();
          cabinet.container.dispose();
          this.frameCabinets();
        }
      }, { signal: this.controller.signal });
      this.restoreSharedDesign();
      if (document.readyState === 'complete') this.prefetchModels();
      else window.addEventListener('load', () => this.prefetchModels(), { once: true, signal: this.controller.signal });
    }

    async prefetchModels() {
      await Promise.all([
        'https://cdn.shopify.com/3d/models/42cde20e9396b8ea/EndPanel_Left.glb',
        'https://cdn.shopify.com/3d/models/363f2b1b056add0d/EndPanel_Right.glb',
      ].map((url) => this.prefetchModel(url)));
      if (this.controller.signal.aborted) return;
      const urls = new Set([...this.querySelectorAll('[data-interior]')].map((button) => button.dataset.interior));
      urls.delete('');
      urls.forEach((url) => this.prefetchModel(url));
    }

    prefetchModel(url) {
      return new Promise((resolve) => {
        const link = document.createElement('link');
        link.rel = 'prefetch';
        link.as = 'fetch';
        link.href = url;
        link.crossOrigin = 'anonymous';
        link.addEventListener('load', resolve, { once: true });
        link.addEventListener('error', () => {
          console.warn(`Model prefetch failed: ${url}`);
          resolve();
        }, { once: true });
        document.head.append(link);
        this.interiorPrefetches.push(link);
      });
    }

    setLoading(message, loading = true) {
      this.designerLoading = message !== '' && loading;
      if (this.designerLoading) Show_Loading(message);
      else Hide_Loading();
      this.querySelector('.designer-placeholder').hidden = loading || message === '';
      this.querySelector('[data-designer-status]').textContent = message;
      this.querySelector('.designer-viewport').setAttribute('aria-busy', String(this.designerLoading));
    }

    initScene() {
      const B = BABYLON;
      const canvas = this.querySelector('canvas');
      this.engine = new B.Engine(canvas, true);
      this.scene = new B.Scene(this.engine);
      this.scene.clearColor = B.Color4.FromHexString('#eef2f5ff');
      this.scene.imageProcessingConfiguration.exposure = 1;
      this.scene.environmentIntensity = .8;
      this.scene.environmentTexture = B.CubeTexture.CreateFromPrefilteredData(
        'https://cdn.shopify.com/s/files/1/0695/9261/3019/files/8276e455-d209-49b8-b9a7-3aa447b8d652.env?v=1787943044',
        this.scene
      );
      this.camera = new B.ArcRotateCamera('island-camera', Math.PI / 2, Math.PI / 2.35, 5, new B.Vector3(0, .45, 0), this.scene);
      this.camera.lowerRadiusLimit = 1.5;
      this.camera.upperRadiusLimit = 30;
      this.camera.upperBetaLimit = Math.PI / 2 - .03;
      this.camera.wheelDeltaPercentage = .01;
      this.camera.minZ = .05;
      this.camera.attachControl(canvas, true);
      this.selectionOutline = new B.HighlightLayer('cabinet-selection', this.scene, {
        mainTextureRatio: 1,
        blurHorizontalSize: .4,
        blurVerticalSize: .4,
      });
      this.selectionOutline.innerGlow = false;
      this.selectionOutline.outerGlow = true;
      const light = new B.HemisphericLight('sky', new B.Vector3(0, 1, 0), this.scene);
      light.intensity = .9;
      const sunlight = new B.DirectionalLight('sun', new B.Vector3(.45, -.65, -1), this.scene);
      sunlight.position.set(-6, 8, 10);
      sunlight.intensity = .35;
      this.countertopMaterial = new B.StandardMaterial('countertop', this.scene);
      this.countertopMaterial.diffuseColor = B.Color3.FromHexString('#eeebe4');
      this.applyCountertop(this.querySelector('[data-countertop][aria-pressed="true"]'));
      this.scene.onPointerObservable.add((info) => {
        if (info.type !== B.PointerEventTypes.POINTERTAP) return;
        const hit = info.pickInfo;
        let cabinet = null;
        if (hit.hit) cabinet = this.cabinets.find((item) => hit.pickedMesh.isDescendantOf(item.root));
        this.selectCabinet(cabinet);
      });
      this.scene.onBeforeRenderObservable.add(() => this.updateEndPanels());
      this.querySelectorAll('[data-model]').forEach((button) => { button.disabled = false; });
      this.setLoading('');
      this.engine.runRenderLoop(() => this.scene.render());
      this.resizeObserver = new ResizeObserver(() => this.engine.resize());
      this.resizeObserver.observe(canvas.parentElement);
    }

    async addCabinet(model, interiorUrl = 'https://cdn.shopify.com/3d/models/626315a52b251814/Interior_Door18_Shelf.glb') {
      this.loadingCabinet = true;
      const B = BABYLON;
      const status = this.querySelector('[data-model-status]');
      const buttons = this.querySelectorAll('[data-model]');
      buttons.forEach((button) => { button.disabled = true; });
      status.textContent = 'Loading cabinet...';
      this.setLoading('Adding your cabinet…');
      let container;
      try {
        if (this.endPanels.length === 0) await this.loadEndPanels();
        if (this.controller.signal.aborted) return;
        container = await B.SceneLoader.LoadAssetContainerAsync('', model, this.scene);
        if (this.controller.signal.aborted) {
          container.dispose();
          return;
        }
        container.animationGroups.forEach((animation) => {
          animation.start(false, 1, animation.from, animation.to);
          animation.pause();
          animation.goToFrame(animation.from);
        });
        const nodes = [...container.transformNodes, ...container.meshes];
        nodes.forEach((node) => {
          if (node.name.includes('RightHinge') ||
              /InteriorPropane|InteriorDrawers/.test(node.name)) node.setEnabled(false);
        });
        this.applyDoorStyle(container);
        const body = container.meshes.find((mesh) => mesh.name.endsWith('_BackPanel'));
        const materials = [...new Set(container.meshes.map((mesh) => mesh.material))]
          .filter((item) => item && item.name === 'HDPE_White');
        const countertop = container.materials.find((item) => item.name === 'Countertop_GlacierFrost');
        if (!body || materials.length === 0 || !countertop) throw new Error('Cabinet model is missing required parts or materials.');
        const root = new B.TransformNode('cabinet', this.scene);
        const modelRoot = new B.TransformNode('cabinet-model', this.scene);
        modelRoot.parent = root;
        [...container.rootNodes].forEach((node) => { node.parent = modelRoot; });
        container.addAllToScene();
        body.computeWorldMatrix(true);
        const bounds = body.getBoundingInfo().boundingBox;
        const width = bounds.maximumWorld.x - bounds.minimumWorld.x;
        modelRoot.position.x = -(bounds.minimumWorld.x + bounds.maximumWorld.x) / 2;
        container.meshes.forEach((mesh) => {
          if (mesh.material === countertop) mesh.material = this.countertopMaterial;
          if (!materials.includes(mesh.material) || mesh.isVerticesDataPresent(B.VertexBuffer.UVKind)) return;
          const positions = mesh.getVerticesData(B.VertexBuffer.PositionKind);
          const normals = mesh.getVerticesData(B.VertexBuffer.NormalKind);
          const uvs = [];
          for (let i = 0; i < positions.length; i += 3) {
            const nx = Math.abs(normals[i]);
            const ny = Math.abs(normals[i + 1]);
            const nz = Math.abs(normals[i + 2]);
            if (ny >= nx && ny >= nz) uvs.push(positions[i], positions[i + 2]);
            else if (nx >= nz) uvs.push(positions[i + 2], positions[i + 1]);
            else uvs.push(positions[i], positions[i + 1]);
          }
          mesh.setVerticesData(B.VertexBuffer.UVKind, uvs);
        });
        materials.forEach((material) => { material.albedoColor = B.Color3.FromHexString('#c7beb7').toLinearSpace(); });
        const cabinet = this.attachCabinet(root, materials, body, width, container, modelRoot, model.includes('/Cabinet_Door18.glb'));
        cabinet.model = model;
        this.applyColor(this.selectedColor);
        status.textContent = '';
        if (cabinet.isDoor18) {
          await this.replaceInterior(cabinet, interiorUrl);
        } else this.setLoading('');
        return cabinet;
      } catch (error) {
        if (container) container.dispose();
        if (!this.controller.signal.aborted) {
          status.textContent = error.message;
          this.setLoading(error.message, false);
        }
        if (this.restoringDesign) throw error;
      } finally {
        this.loadingCabinet = false;
        if (!this.controller.signal.aborted) buttons.forEach((button) => { button.disabled = false; });
      }
    }

    attachCabinet(root, materials, body, width, container, modelRoot, isDoor18) {
      const B = BABYLON;
      const edge = this.cabinets.length === 0 ? -width / 2 : Math.max(
        ...this.cabinets.map((cabinet) => cabinet.root.position.x + cabinet.width / 2)
      );
      root.position.x = edge + width / 2;
      const cabinet = { root, materials, body, width, container, modelRoot, isDoor18, interior: null, interiorUrl: '', interiorLoading: false, isOpen: false, animating: false };
      const drag = new B.PointerDragBehavior({ dragAxis: new B.Vector3(1, 0, 0) });
      drag.useObjectOrientationForDragging = false;
      drag.updateDragPlane = false;
      drag.moveAttached = false;
      let pointerX;
      drag.onDragStartObservable.add(() => {
        this.cabinets.forEach((item) => this.scene.stopAnimation(item.root));
        this.scene.stopAnimation(this.camera);
        this.selectCabinet(cabinet);
        this.camera.detachControl();
        pointerX = root.position.x;
      });
      drag.onDragObservable.add((event) => {
        pointerX += event.delta.x;
        const others = this.cabinets.filter((other) => other !== cabinet);
        const isFree = (x) => others.every((other) =>
          Math.abs(x - other.root.position.x) >= (width + other.width) / 2 - 1e-6
        );
        let x = pointerX;
        let snapDistance = .08;
        others.forEach((other) => {
          const distance = (width + other.width) / 2;
          for (const edge of [other.root.position.x - distance, other.root.position.x + distance]) {
            const gap = Math.abs(pointerX - edge);
            if (gap <= snapDistance && isFree(edge)) {
              snapDistance = gap;
              x = edge;
            }
          }
        });
        if (!isFree(x)) {
          // Stay on the current side until the pointer reaches a free slot.
          let minX = -Infinity;
          let maxX = Infinity;
          others.forEach((other) => {
            const distance = (width + other.width) / 2;
            if (other.root.position.x < root.position.x) {
              minX = Math.max(minX, other.root.position.x + distance);
            } else {
              maxX = Math.min(maxX, other.root.position.x - distance);
            }
          });
          x = Math.max(minX, Math.min(maxX, pointerX));
        }
        root.position.x = x;
      });
      drag.onDragEndObservable.add(() => {
        const left = Math.min(...this.cabinets.map((item) => item.root.position.x - item.width / 2));
        const right = Math.max(...this.cabinets.map((item) => item.root.position.x + item.width / 2));
        const center = (left + right) / 2;
        const easing = new B.CubicEase();
        easing.setEasingMode(B.EasingFunction.EASINGMODE_EASEOUT);
        this.cabinets.forEach((item) => {
          B.Animation.CreateAndStartAnimation('center-cabinet', item.root, 'position.x', 60, 18,
            item.root.position.x, item.root.position.x - center, B.Animation.ANIMATIONLOOPMODE_CONSTANT, easing);
        });
        B.Animation.CreateAndStartAnimation('center-camera', this.camera, 'target', 60, 18,
          this.camera.target.clone(), new B.Vector3(0, this.camera.target.y, this.camera.target.z), B.Animation.ANIMATIONLOOPMODE_CONSTANT, easing);
        this.camera.attachControl(this.querySelector('canvas'), true);
      });
      root.addBehavior(drag);
      this.cabinets.push(cabinet);
      this.selectCabinet(cabinet);
      this.frameCabinets();
      return cabinet;
    }

    selectCabinet(cabinet) {
      this.selectedCabinet = cabinet;
      this.selectionOutline.removeAllMeshes();
      if (cabinet) {
        cabinet.root.getChildMeshes().forEach((mesh) => {
          if (cabinet.interior && mesh.isDescendantOf(cabinet.interior.root)) return;
          if (mesh.getTotalVertices() > 0) this.selectionOutline.addMesh(mesh, BABYLON.Color3.FromHexString('#e5ef00'));
        });
      }
      const busy = cabinet && (cabinet.animating || cabinet.interiorLoading);
      this.querySelector('[data-delete-selected]').disabled = !cabinet || busy;
      const animatedCabinets = this.cabinets.filter((item) => item.container.animationGroups.some((animation) => animation.name === 'Open'));
      this.querySelector('[data-door-controls]').hidden = animatedCabinets.length === 0;
      const doorButton = this.querySelector('[data-toggle-door]');
      doorButton.disabled = animatedCabinets.some((item) => item.animating || item.interiorLoading);
      doorButton.textContent = animatedCabinets.some((item) => item.isOpen) ? 'Close All Doors' : 'Open All Doors';
      this.querySelector('[data-selected-door-controls]').hidden = !animatedCabinets.includes(cabinet);
      const selectedDoorButton = this.querySelector('[data-toggle-selected-door]');
      selectedDoorButton.disabled = !cabinet || busy;
      selectedDoorButton.textContent = cabinet && cabinet.isOpen ? 'Close Selected Doors' : 'Open Selected Doors';
      this.querySelector('[data-interior-options]').hidden = !cabinet || !cabinet.isDoor18;
      this.querySelectorAll('[data-interior]').forEach((button) => {
        const selected = !!cabinet && button.dataset.interior === cabinet.interiorUrl;
        button.setAttribute('aria-pressed', String(selected));
        button.disabled = !cabinet || busy;
        if (selected) this.querySelector('[data-interior-name]').textContent = button.getAttribute('aria-label');
      });
    }

    playClip(container, name, forward) {
      const animation = container.animationGroups.find((group) => group.name === name);
      if (!animation) throw new Error(`Model animation is missing: ${name}`);
      return new Promise((resolve) => {
        animation.stop();
        animation.onAnimationGroupEndObservable.addOnce(resolve);
        animation.start(false, 2, forward ? animation.from : animation.to, forward ? animation.to : animation.from);
      });
    }

    async toggleAllDoors() {
      const cabinets = this.cabinets.filter((item) => item.container.animationGroups.some((animation) => animation.name === 'Open'));
      if (cabinets.some((item) => item.animating || item.interiorLoading)) return;
      const opening = !cabinets.some((item) => item.isOpen);
      await Promise.all(cabinets.filter((item) => item.isOpen !== opening).map((item) => this.setDoors(item, opening)));
    }

    async setDoors(cabinet, opening) {
      if (!cabinet || cabinet.animating || cabinet.interiorLoading) return;
      cabinet.animating = true;
      this.selectCabinet(this.selectedCabinet);
      try {
        const slidingInterior = cabinet.interior && cabinet.interior.container.animationGroups.some((animation) => animation.name === 'SlideOut');
        if (!opening && slidingInterior) await this.playClip(cabinet.interior.container, 'SlideOut', false);
        if (this.controller.signal.aborted) return;
        await this.playClip(cabinet.container, 'Open', opening);
        if (this.controller.signal.aborted) return;
        cabinet.isOpen = opening;
        if (opening && slidingInterior) await this.playClip(cabinet.interior.container, 'SlideOut', true);
      } catch (error) {
        if (!this.controller.signal.aborted) this.querySelector('[data-model-status]').textContent = error.message;
      } finally {
        cabinet.animating = false;
        if (!this.controller.signal.aborted) this.selectCabinet(this.selectedCabinet);
      }
    }

    async loadPart(url) {
      const container = await BABYLON.SceneLoader.LoadAssetContainerAsync('', url, this.scene);
      if (this.controller.signal.aborted) {
        container.dispose();
        return null;
      }
      const root = new BABYLON.TransformNode('island-part', this.scene);
      root.setEnabled(false);
      [...container.rootNodes].forEach((node) => { node.parent = root; });
      container.addAllToScene();
      container.animationGroups.forEach((animation) => {
        if (url.endsWith('/Interior_Door18_Drawers.glb') && animation.name === 'SlideOut') {
          // Delay the upper drawer by 0.25 seconds at the current 2x playback speed.
          animation.targetedAnimations.forEach(({ animation: track, target }) => {
            const keys = track.getKeys();
            const firstMove = keys.findIndex((key, index) => index > 0 && !key.value.equalsWithEpsilon(keys[index - 1].value, 0.00001));
            if (firstMove === -1) return;
            let lastMove = keys.length - 1;
            while (lastMove > firstMove && keys[lastMove].value.equalsWithEpsilon(keys[lastMove - 1].value, 0.00001)) lastMove--;
            const start = keys[firstMove - 1].frame;
            const delay = target.name.endsWith('_Upper') ? 0.25 * 2 * track.framePerSecond : 0;
            track.setKeys(keys.slice(firstMove - 1, lastMove + 1).map((key) => ({ ...key, frame: key.frame - start + delay })));
          });
          const end = Math.max(...animation.targetedAnimations.map(({ animation: track }) => track.getKeys().at(-1).frame));
          animation.normalize(0, end);
        }
        animation.start(false, 1, animation.from, animation.to);
        animation.pause();
        animation.goToFrame(animation.from);
      });
      container.meshes.forEach((mesh) => {
        mesh.isPickable = false;
        if (mesh.material && mesh.material.name === 'Countertop_GlacierFrost') mesh.material = this.countertopMaterial;
      });
      return { root, container };
    }

    async loadEndPanels() {
      const panels = [];
      try {
        for (const path of ['42cde20e9396b8ea/EndPanel_Left', '363f2b1b056add0d/EndPanel_Right']) {
          const panel = await this.loadPart(`https://cdn.shopify.com/3d/models/${path}.glb`);
          if (!panel) {
            panels.forEach((item) => { item.root.dispose(); item.container.dispose(); });
            return;
          }
          panel.finishMeshes = panel.container.meshes.filter((mesh) => mesh.material && mesh.material.name === 'HDPE_White');
          panels.push(panel);
        }
        this.endPanels = panels;
      } catch (error) {
        panels.forEach((item) => { item.root.dispose(); item.container.dispose(); });
        throw error;
      }
    }

    updateEndPanels() {
      if (this.endPanels.length !== 2) return;
      const showPanels = this.querySelector('[data-end-panels]').checked && this.cabinets.length > 0;
      this.endPanels.forEach((panel) => panel.root.setEnabled(showPanels));
      if (this.cabinets.length === 0) return;
      const order = [...this.cabinets].sort((a, b) => a.root.position.x - b.root.position.x);
      const owners = [order[0], order[order.length - 1]];
      this.endPanels.forEach((panel, index) => {
        const owner = owners[index];
        panel.root.position.set(owner.root.position.x + (index === 0 ? -owner.width / 2 : owner.width / 2),
          0, owner.modelRoot.position.z);
        panel.finishMeshes.forEach((mesh) => {
          mesh.material = owner.materials[0];
        });
      });
    }

    async replaceInterior(cabinet, url) {
      if (!cabinet || !cabinet.isDoor18 || cabinet.interiorLoading || cabinet.animating) return;
      const status = this.querySelector('[data-model-status]');
      cabinet.interiorLoading = true;
      this.selectCabinet(this.selectedCabinet);
      status.textContent = 'Loading interior...';
      this.setLoading('Loading cabinet interior…');
      try {
        const part = url === '' ? null : await this.loadPart(url);
        if (this.controller.signal.aborted || !this.cabinets.includes(cabinet)) {
          if (part) { part.root.dispose(); part.container.dispose(); }
          return;
        }
        if (cabinet.interior) {
          cabinet.interior.root.dispose();
          cabinet.interior.container.dispose();
        }
        cabinet.interior = part;
        cabinet.interiorUrl = url;
        if (part) {
          part.root.parent = cabinet.modelRoot;
          part.container.meshes.forEach((mesh) => {
            if (mesh.getTotalVertices() === 0) return;
            this.selectionOutline.addExcludedMesh(mesh);
            mesh.onDisposeObservable.addOnce(() => this.selectionOutline.removeExcludedMesh(mesh));
          });
          part.root.setEnabled(true);
          if (cabinet.isOpen && part.container.animationGroups.some((animation) => animation.name === 'SlideOut')) {
            await this.playClip(part.container, 'SlideOut', true);
          }
        }
        status.textContent = '';
        this.setLoading('');
      } catch (error) {
        if (!this.controller.signal.aborted) {
          status.textContent = error.message;
          this.setLoading(error.message, false);
        }
        if (this.restoringDesign) throw error;
      } finally {
        cabinet.interiorLoading = false;
        if (!this.controller.signal.aborted) this.selectCabinet(this.selectedCabinet);
      }
    }

    frameCabinets() {
      if (this.cabinets.length === 0) return;
      const left = Math.min(...this.cabinets.map((item) => item.root.position.x - item.width / 2));
      const right = Math.max(...this.cabinets.map((item) => item.root.position.x + item.width / 2));
      const bounds = this.cabinets.map((item) => item.root.getHierarchyBoundingVectors(true, (mesh) => mesh.isEnabled()));
      const minY = Math.min(...bounds.map((box) => box.min.y));
      const maxY = Math.max(...bounds.map((box) => box.max.y));
      const minZ = Math.min(...bounds.map((box) => box.min.z));
      const maxZ = Math.max(...bounds.map((box) => box.max.z));
      this.camera.setTarget(new BABYLON.Vector3((left + right) / 2, (minY + maxY) / 2, (minZ + maxZ) / 2));
      this.camera.radius = Math.max(3, (right - left) * 1.6);
    }

    applyCountertop(button) {
      const material = this.countertopMaterial;
      if (material.diffuseTexture) material.diffuseTexture.dispose();
      material.diffuseTexture = new BABYLON.Texture(button.dataset.countertop, this.scene);
      material.diffuseColor = BABYLON.Color3.White();
      this.querySelectorAll('[data-countertop]').forEach((option) => {
        option.setAttribute('aria-pressed', String(option === button));
      });
    }

    applyDoorStyle(container) {
      [...container.transformNodes, ...container.meshes].forEach((node) => {
        const style = node.name.match(/_(Sanibel|Miami)$/);
        if (style) node.setEnabled(style[1] === this.doorStyle);
      });
    }

    applyColor(button) {
      if (button === null) button = this.querySelector('[data-color="#c7beb7"]');
      this.selectedColor = button;
      this.querySelectorAll('[data-color]').forEach((option) => {
        option.setAttribute('aria-pressed', String(option === button));
      });
      this.updateDoorPreviews();
      this.cabinets.flatMap((cabinet) => cabinet.materials).forEach((material) => {
        if (material.albedoTexture) material.albedoTexture.dispose();
        material.albedoTexture = null;
        if (button && button.dataset.color.startsWith('WOOD')) {
          const url = button.style.backgroundImage.slice(5, -2);
          material.albedoTexture = new BABYLON.Texture(url, this.scene);
          material.albedoColor = BABYLON.Color3.White();
        } else {
          material.albedoColor = BABYLON.Color3.FromHexString(button ? button.dataset.color : '#c7beb7').toLinearSpace();
        }
      });
    }

    async loadPreviewProduct(handle) {
      if (!this.previewProducts.has(handle)) {
        this.previewProducts.set(handle, fetch(`${window.Shopify.routes.root}products/${encodeURIComponent(handle)}?view=island-builder-data`, {
          credentials: 'same-origin', signal: this.controller.signal,
        }).then(async (response) => {
          if (!response.ok) throw new Error(`Unable to load door style previews: ${handle}.`);
          return response.json();
        }));
      }
      return this.previewProducts.get(handle);
    }

    async updateDoorPreviews() {
      const version = ++this.previewVersion;
      const color = this.selectedColor ? this.selectedColor.getAttribute('aria-label') : 'Shoreline Gray';
      const normalize = (value) => value.trim().toLowerCase();
      try {
        const data = await this.loadPreviewProduct('af685lpp-stk');
        const previews = await Promise.all([...this.querySelectorAll('[data-door-preview]')].map(async (image) => {
          let product = [data.product, ...data.mappings.door_style_mapping].find((item) => item.style === image.dataset.doorPreview);
          if (!product) throw new Error(`Door style preview is missing: ${image.dataset.doorPreview}.`);
          if (normalize(product.color) !== normalize(color)) {
            const styleData = await this.loadPreviewProduct(product.handle);
            product = styleData.mappings.color_mapping.find((item) => normalize(item.color) === normalize(color));
          }
          if (!product || !product.doorStyleImage) throw new Error(`Door style preview is unavailable for ${color}.`);
          return { image, url: product.doorStyleImage };
        }));
        if (version === this.previewVersion && !this.controller.signal.aborted) {
          previews.forEach(({ image, url }) => { image.src = url; });
        }
      } catch (error) {
        if (version === this.previewVersion && !this.controller.signal.aborted) this.querySelector('[data-model-status]').textContent = error.message;
      }
    }

    clearARPreview(releaseModel = false) {
      cancelAnimationFrame(this.arRestartFrame);
      if (this.arViewerController) this.arViewerController.abort();
      this.arViewer = null;
      this.querySelector('[data-ar-preview]').replaceChildren();
      if (releaseModel) {
        if (this.arUrl) URL.revokeObjectURL(this.arUrl);
        this.arUrl = null;
        this.arDesignKey = null;
      }
    }

    designOptions() {
      return {
        models: [...this.querySelectorAll('[data-model]')].map((button) => button.dataset.model),
        interiors: [...this.querySelectorAll('[data-interior]')].map((button) => button.dataset.interior),
        colors: [...this.querySelectorAll('[data-color]')],
        countertops: [...this.querySelectorAll('[data-countertop]')],
      };
    }

    createDesignLink() {
      const options = this.designOptions();
      const design = {
        v: 1,
        cabinets: this.cabinets.map((cabinet) => [options.models.indexOf(cabinet.model),
          Math.round(cabinet.root.position.x * 1000000) / 1000000,
          options.interiors.indexOf(cabinet.interiorUrl), cabinet.isOpen ? 1 : 0]),
        color: options.colors.indexOf(this.selectedColor),
        countertop: options.countertops.indexOf(this.querySelector('[data-countertop][aria-pressed="true"]')),
        style: this.doorStyle,
        panels: this.querySelector('[data-end-panels]').checked,
      };
      const url = new URL(window.location.href);
      url.hash = `design=${btoa(JSON.stringify(design)).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '')}`;
      return url.href;
    }

    async restoreSharedDesign() {
      const encoded = new URLSearchParams(window.location.hash.slice(1)).get('design');
      if (encoded === null) return;
      const status = this.querySelector('[data-model-status]');
      const sidebar = this.querySelector('.designer-sidebar');
      this.restoringDesign = true;
      sidebar.inert = true;
      this.querySelector('.designer-viewport').inert = true;
      this.camera.detachControl();
      try {
        const design = JSON.parse(atob(encoded.replace(/-/g, '+').replace(/_/g, '/')));
        const options = this.designOptions();
        const validIndex = (value, items) => Number.isInteger(value) && value >= 0 && value < items.length;
        if (design.v !== 1 || !Array.isArray(design.cabinets) || design.cabinets.length === 0 ||
            !Number.isInteger(design.color) || design.color < -1 || design.color >= options.colors.length ||
            !validIndex(design.countertop, options.countertops) || !['Sanibel', 'Miami'].includes(design.style) ||
            typeof design.panels !== 'boolean' || design.cabinets.some((item) => !Array.isArray(item) || item.length !== 4 ||
              !validIndex(item[0], options.models) || !Number.isFinite(item[1]) ||
              !validIndex(item[2], options.interiors) || ![0, 1].includes(item[3]) ||
              (!options.models[item[0]].includes('/Cabinet_Door18.glb') && item[2] !== 0))) {
          throw new Error('This shared design is invalid or uses an unsupported version.');
        }
        this.doorStyle = design.style;
        this.querySelector('[data-door-style-name]').textContent = design.style;
        this.querySelectorAll('[data-door-style]').forEach((button) => {
          button.setAttribute('aria-pressed', String(button.dataset.doorStyle === design.style));
        });
        this.querySelector('[data-end-panels]').checked = design.panels;
        this.applyColor(design.color === -1 ? null : options.colors[design.color]);
        options.colors.forEach((button) => button.setAttribute('aria-pressed', String(button === this.selectedColor)));
        this.applyCountertop(options.countertops[design.countertop]);
        for (const [index, x, interiorIndex, open] of design.cabinets) {
          if (this.controller.signal.aborted) return;
          const cabinet = await this.addCabinet(options.models[index], options.interiors[interiorIndex]);
          if (this.controller.signal.aborted) return;
          if (!cabinet) throw new Error('Unable to restore a cabinet in this design.');
          cabinet.root.position.x = x;
          cabinet.isOpen = open === 1;
          cabinet.container.animationGroups.forEach((animation) => animation.goToFrame(open ? animation.to : animation.from));
          if (cabinet.interior) cabinet.interior.container.animationGroups.forEach((animation) => animation.goToFrame(open ? animation.to : animation.from));
        }
        this.updateEndPanels();
        this.selectCabinet(null);
        this.frameCabinets();
        status.textContent = 'Your shared design is ready. Tap View in AR to place it.';
      } catch (error) {
        if (!this.controller.signal.aborted) {
          this.cabinets.forEach((cabinet) => {
            cabinet.root.dispose();
            if (cabinet.interior) cabinet.interior.container.dispose();
            cabinet.container.dispose();
          });
          this.cabinets = [];
          this.selectCabinet(null);
          status.textContent = `Unable to load shared design: ${error.message}`;
        }
      } finally {
        this.restoringDesign = false;
        if (!this.controller.signal.aborted) {
          sidebar.inert = false;
          this.querySelector('.designer-viewport').inert = false;
          this.camera.attachControl(this.querySelector('canvas'), true);
        }
      }
      if (!this.controller.signal.aborted && this.cabinets.length > 0 && window.matchMedia('(pointer: coarse)').matches) this.openAR(true);
    }

    async showARCode() {
      const dialog = this.querySelector('.designer-ar');
      const status = this.querySelector('[data-ar-status]');
      const button = this.querySelector('[data-view-ar]');
      dialog.showModal();
      this.clearARPreview();
      status.textContent = 'Preparing a QR code for your current design...';
      button.disabled = true;
      try {
        const link = this.createDesignLink();
        if (!this.qrScript) this.qrScript = BABYLON.Tools.LoadScriptAsync('https://cdn.jsdelivr.net/npm/qrcode-generator@1.4.4/qrcode.js');
        await this.qrScript;
        if (this.controller.signal.aborted || !dialog.open) return;
        const code = window.qrcode(0, 'M');
        code.addData(link);
        code.make();
        const preview = this.querySelector('[data-ar-preview]');
        const qr = document.createElement('div');
        qr.className = 'designer-qr';
        qr.setAttribute('role', 'img');
        qr.setAttribute('aria-label', 'Scan to open this kitchen design on your phone');
        qr.innerHTML = code.createSvgTag({ cellSize: 6, margin: 24, scalable: true });
        const open = document.createElement('a');
        open.href = link;
        open.textContent = 'Open this design on another device';
        const copy = document.createElement('button');
        copy.type = 'button';
        copy.textContent = 'Copy link';
        copy.addEventListener('click', async () => {
          try { await navigator.clipboard.writeText(link); copy.textContent = 'Link copied'; }
          catch { status.textContent = 'Copy the design link using the link above.'; }
        }, { signal: this.controller.signal });
        const desktopPreview = document.createElement('button');
        desktopPreview.type = 'button';
        desktopPreview.textContent = 'Preview on this device';
        desktopPreview.addEventListener('click', () => this.openAR(true), { signal: this.controller.signal });
        preview.append(qr, open, copy, desktopPreview);
        status.textContent = 'Scan with your phone camera to load this exact layout, finishes and options. No rebuilding required.';
      } catch (error) {
        if (!this.controller.signal.aborted) status.textContent = `Unable to create QR code: ${error.message}`;
      } finally {
        if (!this.controller.signal.aborted) button.disabled = false;
      }
    }

    mountARViewer() {
      const status = this.querySelector('[data-ar-status]');
      if (this.arViewerController) this.arViewerController.abort();
      this.arViewerController = new AbortController();
      const signal = this.arViewerController.signal;
      this.querySelector('[data-ar-preview]').replaceChildren();
      const viewer = document.createElement('model-viewer');
      this.arViewer = viewer;
      let sessionStarted = false;
      viewer.setAttribute('ar', '');
      viewer.setAttribute('ar-modes', 'webxr quick-look');
      viewer.setAttribute('camera-controls', '');
      viewer.setAttribute('ar-placement', 'floor');
      viewer.setAttribute('alt', 'Your configured outdoor kitchen');
      const launch = document.createElement('button');
      launch.type = 'button';
      launch.slot = 'ar-button';
      launch.textContent = 'View in AR';
      launch.disabled = true;
      launch.addEventListener('click', async (event) => {
        // Activate directly from this gesture, without a second bubbled launch.
        event.preventDefault();
        event.stopPropagation();
        launch.disabled = true;
        try {
          await viewer.activateAR();
        } catch (error) {
          if (!signal.aborted) status.textContent = `Unable to start AR: ${error.message}`;
        } finally {
          if (!signal.aborted) launch.disabled = false;
        }
      }, { signal });
      viewer.append(launch);
      viewer.addEventListener('load', () => {
        launch.disabled = !viewer.canActivateAR;
        status.textContent = viewer.canActivateAR
          ? 'Tap View in AR to place this configuration. Options are fixed in AR.'
          : 'AR is unavailable in this browser. Open this page in Safari on iPhone or an AR-enabled Chrome browser on Android.';
      }, { signal });
      viewer.addEventListener('error', () => {
        status.textContent = 'Unable to load the AR model. Close and try again.';
      }, { signal });
      viewer.addEventListener('ar-status', (event) => {
        if (event.detail.status === 'session-started') sessionStarted = true;
        if (event.detail.status === 'failed') {
          launch.disabled = false;
          status.textContent = 'Unable to start AR. Check camera permission and device support.';
        }
        if (event.detail.status === 'not-presenting' && sessionStarted) {
          sessionStarted = false;
          launch.disabled = true;
          status.textContent = 'AR closed. Preparing to view again...';
          this.arRestartFrame = requestAnimationFrame(() => {
            if (!signal.aborted && this.querySelector('.designer-ar').open && this.arViewer === viewer) {
              this.mountARViewer();
            }
          });
        }
      }, { signal });
      viewer.src = this.arUrl;
      this.querySelector('[data-ar-preview]').append(viewer);
    }

    async openAR(previewOnDevice = false) {
      const dialog = this.querySelector('.designer-ar');
      const status = this.querySelector('[data-ar-status]');
      const button = this.querySelector('[data-view-ar]');
      if (button.disabled) return;
      if (this.restoringDesign || this.loadingCabinet || this.cabinets.some((cabinet) => cabinet.animating || cabinet.interiorLoading)) {
        this.querySelector('[data-model-status]').textContent = 'Please wait for the current cabinet or animation to finish.';
        return;
      }
      if (this.cabinets.length === 0) {
        this.querySelector('[data-model-status]').textContent = 'Add a cabinet before opening AR.';
        return;
      }
      if (!previewOnDevice && !window.matchMedia('(pointer: coarse)').matches) {
        await this.showARCode();
        return;
      }
      if (!dialog.open) dialog.showModal();
      this.clearARPreview();
      const designKey = this.createDesignLink();
      if (this.arUrl && this.arDesignKey === designKey) {
        status.textContent = 'Loading your AR preview...';
        this.mountARViewer();
        return;
      }
      this.clearARPreview(true);
      status.textContent = 'Preparing your outdoor kitchen for AR...';
      button.disabled = true;
      const animationsEnabled = this.scene.animationsEnabled;
      this.scene.animationsEnabled = false;
      try {
        if (!this.arLibraries) {
          this.arLibraries = Promise.all([
            import('https://unpkg.com/@google/model-viewer@4.2.0/dist/model-viewer.min.js'),
            BABYLON.Tools.LoadScriptAsync('https://cdn.babylonjs.com/serializers/babylonjs.serializers.min.js'),
          ]).catch((error) => { this.arLibraries = null; throw error; });
        }
        await this.arLibraries;
        if (this.controller.signal.aborted || !dialog.open) return;
        await this.scene.whenReadyAsync();
        if (this.controller.signal.aborted || !dialog.open) return;
        this.updateEndPanels();
        const nodes = new Set();
        [...this.cabinets, ...this.endPanels].forEach(({ root }) => {
          nodes.add(root);
          root.getDescendants().forEach((node) => nodes.add(node));
        });
        const exported = await BABYLON.GLTF2Export.GLBAsync(this.scene, 'outdoor-kitchen-ar', {
          shouldExportNode: (node) => nodes.has(node) && node.isEnabled() &&
            (!(node instanceof BABYLON.AbstractMesh) || node.isVisible && node.visibility > 0),
          shouldExportAnimation: () => false,
        });
        if (this.controller.signal.aborted || !dialog.open) return;
        this.arUrl = URL.createObjectURL(exported.files['outdoor-kitchen-ar.glb']);
        this.arDesignKey = designKey;
        this.mountARViewer();
      } catch (error) {
        if (!this.controller.signal.aborted) status.textContent = `Unable to prepare AR: ${error.message}`;
      } finally {
        if (!this.controller.signal.aborted) {
          this.scene.animationsEnabled = animationsEnabled;
          button.disabled = false;
        }
      }
    }

    disconnectedCallback() {
      if (this.designerLoading) Hide_Loading();
      this.clearARPreview(true);
      this.controller.abort();
      this.interiorPrefetches.forEach((link) => link.remove());
      cancelAnimationFrame(this.sidebarFrame);
      this.headerObserver.disconnect();
      if (this.resizeObserver) this.resizeObserver.disconnect();
      if (this.scene) this.scene.dispose();
      if (this.engine) this.engine.dispose();
    }
  });
}
